import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Barbell } from "@phosphor-icons/react";
import { toast } from "sonner";

// Google sign-in return point. Emergent OAuth redirects back here with
// `#session_id=<one-time-token>` in the URL fragment. We hand that off
// to the backend exactly once, which validates it with Emergent and
// (if the email matches an invited user) issues our regular JWT.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleCallback() {
  const { loginWithGoogleSession } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // useRef, not useState — the exchange must fire exactly once even
  // under React StrictMode (which double-invokes effects in dev).
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("Google sign-in didn't return a session. Please try again.");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      const res = await loginWithGoogleSession(sessionId);
      if (!res.ok) {
        setError(res.error || "Sign-in failed");
        return;
      }
      // Clear the one-time session_id from the URL so a browser back-nav
      // can't replay it, then land the user inside the app.
      window.history.replaceState({}, "", "/");
      toast.success("Welcome back");
      nav("/", { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid place-items-center bg-bone px-6">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto h-12 w-12 rounded-xl bg-moss text-white grid place-items-center">
          <Barbell size={22} weight="bold" />
        </div>
        {error ? (
          <>
            <h1 className="font-heading text-2xl font-semibold text-ink">
              Can't sign in
            </h1>
            <p className="text-sm text-ink-2" data-testid="google-callback-error">
              {error}
            </p>
            <button
              type="button"
              onClick={() => nav("/login", { replace: true })}
              className="h-11 px-6 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white font-medium"
              data-testid="google-callback-back"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold text-ink">
              Signing you in…
            </h1>
            <p className="text-sm text-ink-3">One moment while we verify your Google account.</p>
          </>
        )}
      </div>
    </div>
  );
}
